import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Package } from 'lucide-react';
import type { SourcingRequirement } from '@tailoring-crm/shared';
import { useAuth } from '../../lib/auth/auth-context.js';
import { formatCurrency } from '../../lib/format.js';
import { useOrderSourcing } from '../../lib/sourcing/use-sourcing.js';
import { Button, ListSkeleton } from '@tailoring-crm/ui';
import { SourcingFormDialog, type SourcingFormItem } from './sourcing-form-dialog.js';
import { SourcingRow } from './sourcing-row.js';

/**
 * What this order needs, on the screen where somebody is looking at the order.
 *
 * The card exists because neither module beside it can answer the question. Purchasing knows what
 * was ordered from a supplier; stock knows what is on the shelf; neither says that THIS lehenga is
 * waiting on six metres of raw silk and a card of matching buttons. That is the question asked with
 * the customer on the phone wanting to know when it will be ready.
 *
 * THE TOTAL IS WHAT IS STILL EXPECTED TO BE SPENT — the API sums the non-cancelled rows only, so a
 * need that was dropped when the design changed stops counting against the job.
 *
 * A tailor sees none of it. Sourcing carries an expected cost in rupees, both permissions are money
 * and the matrix refuses a karigar every route — rendering a card whose every button 403s would be
 * worse than rendering nothing, so the card returns null and never asks.
 */
export interface SourcingCardProps {
  orderId: string;
  orderNumber: string;
  /** The garments of this order, for the form's garment field. */
  items: readonly SourcingFormItem[];
}

export function SourcingCard({
  orderId,
  orderNumber,
  items,
}: SourcingCardProps): ReactElement | null {
  const { t } = useTranslation();
  const { role } = useAuth();

  const sourcing = useOrderSourcing(role === 'tailor' ? null : orderId);

  const [form, setForm] = useState<{ requirement: SourcingRequirement | null } | null>(null);

  if (role === 'tailor') return null;

  const rows = sourcing.data?.items ?? [];
  const totalPaise = sourcing.data?.totalPaise ?? 0;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{t('sourcing.heading')}</h3>
          <p className="text-sm text-muted-foreground">
            {rows.length === 0
              ? t('sourcing.none')
              : t('sourcing.cardTotal', { amount: formatCurrency(totalPaise) })}
          </p>
        </div>

        <Button size="sm" onClick={() => setForm({ requirement: null })}>
          <Package aria-hidden="true" />
          {t('sourcing.new')}
        </Button>
      </div>

      {sourcing.isPending ? <ListSkeleton rows={2} /> : null}

      {rows.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {rows.map((requirement) => (
            <SourcingRow
              key={requirement.id}
              requirement={requirement}
              onEdit={(row) => setForm({ requirement: row })}
            />
          ))}
        </ul>
      ) : null}

      {form ? (
        <SourcingFormDialog
          open
          onOpenChange={(open) => !open && setForm(null)}
          orderId={orderId}
          orderNumber={orderNumber}
          items={items}
          requirement={form.requirement}
          onSaved={() => setForm(null)}
        />
      ) : null}
    </section>
  );
}

export default SourcingCard;
